// Hand-written API Gateway client for the netcountable-e2ee service (NC1 key
// layer), following the same factory/makeRequest conventions as the generated
// clients in src/aws/* (see src/aws/survey/apigClient.js for the canonical
// generated shape). SigV4-signed via the shared apiGatewayCore.

import utils from '../apiGatewayCore/utils';
import { apiGatewayClientFactory } from '../apiGatewayCore/apiGatewayClient.js'
import uritemplate from "../apiGatewayCore/url-template"
import { queryParamsFor } from './queryParams'

// Session-wide query params, sent on EVERY operation when they have a value:
// deviceUUID is the caller's transport identity inside the app's WebView (the
// service's callerIdentityId reads it to find the device row that names the
// account). E2eeApi supplies it through options.sessionQueryParams, so no
// operation has to list it and none can forget it.
var SESSION_PARAMS = ['deviceUUID'];

var apigClientFactory = {};
apigClientFactory.newClient = function (config, invokeUrl, options) {
    var apigClient = { };
    var sessionQueryParams = (options && options.sessionQueryParams) || function () { return {}; };
    if(config === undefined) {
        config = {
            accessKey: '',
            secretKey: '',
            sessionToken: '',
            region: '',
            apiKey: undefined,
            defaultContentType: 'application/json',
            defaultAcceptType: 'application/json'
        };
    }
    if(config.accessKey === undefined)  { config.accessKey = ''; }
    if(config.secretKey === undefined)  { config.secretKey = ''; }
    if(config.apiKey === undefined)     { config.apiKey = ''; }
    if(config.sessionToken === undefined) { config.sessionToken = ''; }
    if(config.region === undefined)     { config.region = 'us-east-1'; }
    if(config.defaultContentType === undefined) { config.defaultContentType = 'application/json'; }
    if(config.defaultAcceptType === undefined)  { config.defaultAcceptType = 'application/json'; }

    // extract endpoint and path from url
    var endpoint = /(^https?:\/\/[^\/]+)/g.exec(invokeUrl)[1];
    var pathComponent = invokeUrl.substring(endpoint.length);

    var sigV4ClientConfig = {
        accessKey: config.accessKey,
        secretKey: config.secretKey,
        sessionToken: config.sessionToken,
        serviceName: 'execute-api',
        region: config.region,
        endpoint: endpoint,
        defaultContentType: config.defaultContentType,
        defaultAcceptType: config.defaultAcceptType
    };

    var authType = 'NONE';
    if (sigV4ClientConfig.accessKey !== undefined && sigV4ClientConfig.accessKey !== ''
        && sigV4ClientConfig.secretKey !== undefined && sigV4ClientConfig.secretKey !== '') {
        authType = 'AWS_IAM';
    }

    var simpleHttpClientConfig = {
        endpoint: endpoint,
        defaultContentType: config.defaultContentType,
        defaultAcceptType: config.defaultAcceptType
    };

    var apiGatewayClient = apiGatewayClientFactory.newClient(simpleHttpClientConfig, sigV4ClientConfig);

    // All e2ee routes share the same request shape: query params from the
    // operation's whitelist (plus SESSION_PARAMS), JSON body on writes,
    // optional {pathParam} segments (e.g. /e2ee/enrollments/{requestId}/accept).
    // Unlike the generated clients, a whitelisted key the caller did not set is
    // NOT sent (queryParams.js) - no "from=undefined" on the wire.
    var makeOperation = function (verb, path, queryParamNames, pathParamNames) {
        if(pathParamNames === undefined) { pathParamNames = []; }
        var allQueryParamNames = queryParamNames.concat(SESSION_PARAMS);
        return function (params, body, additionalParams) {
            if(additionalParams === undefined) { additionalParams = {}; }

            utils.assertParametersDefined(params, pathParamNames, ['body']);

            var request = {
                verb: verb.toUpperCase(),
                path: pathComponent + uritemplate(path).expand(utils.parseParametersToObject(params, pathParamNames)),
                headers: utils.parseParametersToObject(params, []),
                queryParams: queryParamsFor(params, allQueryParamNames, sessionQueryParams()),
                body: body
            };

            return apiGatewayClient.makeRequest(request, authType, additionalParams, config.apiKey);
        };
    };

    // Each whitelist names what the operation itself takes; deviceUUID rides
    // along from SESSION_PARAMS (until 2026-10-14 it was left off on purpose,
    // because an absent key used to serialize as "deviceUUID=undefined" - and
    // the app's WebView, which does have one, could not read the key layer).
    var READ_PARAMS = ['email'];
    var WRITE_PARAMS = ['email'];

    apigClient.e2eeAccountGet = makeOperation('get', '/e2ee/account', READ_PARAMS);
    apigClient.e2eeResolveGet = makeOperation('get', '/e2ee/resolve', READ_PARAMS);
    apigClient.e2eeAccountPost = makeOperation('post', '/e2ee/account', WRITE_PARAMS);
    apigClient.e2eeAccountResetPost = makeOperation('post', '/e2ee/account/reset', WRITE_PARAMS);
    apigClient.e2eeIdentityBundleGet = makeOperation('get', '/e2ee/identityBundle', READ_PARAMS);
    apigClient.e2eeIdentityBundlePost = makeOperation('post', '/e2ee/identityBundle', WRITE_PARAMS);
    apigClient.e2eeDeviceListGet = makeOperation('get', '/e2ee/deviceList', READ_PARAMS);
    apigClient.e2eeDeviceListPost = makeOperation('post', '/e2ee/deviceList', WRITE_PARAMS);
    apigClient.e2eeMemberListGet = makeOperation('get', '/e2ee/memberList', READ_PARAMS);
    apigClient.e2eeMemberListPost = makeOperation('post', '/e2ee/memberList', WRITE_PARAMS);
    apigClient.e2eeAdkEnvelopesGet = makeOperation('get', '/e2ee/adkEnvelopes', READ_PARAMS);
    apigClient.e2eeAdkEnvelopesPost = makeOperation('post', '/e2ee/adkEnvelopes', WRITE_PARAMS);
    apigClient.e2eeLogGet = makeOperation('get', '/e2ee/log', ['email', 'from']);
    apigClient.e2eeLogPost = makeOperation('post', '/e2ee/log', WRITE_PARAMS);
    apigClient.e2eeRecordsPurgePost = makeOperation('post', '/e2ee/records/purge', WRITE_PARAMS);
    // 2026-09-14: the purge is asynchronous — POST answers 202 + jobId and
    // this polls the job row (its own operation so jobId is only ever sent here).
    apigClient.e2eeRecordsPurgeGet = makeOperation('get', '/e2ee/records/purge', ['email', 'jobId']);

    // Device-transfer enrollments. The requester poll and the approver list
    // hit the same GET route but stay separate operations: `deviceUuid` (the
    // link request's own id, not the transport deviceUUID above) is what flips
    // the server into poll mode, so only the poll may ever carry it.
    apigClient.e2eeEnrollmentsPost = makeOperation('post', '/e2ee/enrollments', WRITE_PARAMS);
    apigClient.e2eeEnrollmentsListGet = makeOperation('get', '/e2ee/enrollments', READ_PARAMS);
    apigClient.e2eeEnrollmentsPollGet = makeOperation('get', '/e2ee/enrollments', ['email', 'deviceUuid']);
    apigClient.e2eeEnrollmentAcceptPost = makeOperation('post', '/e2ee/enrollments/{requestId}/accept', WRITE_PARAMS, ['requestId']);
    apigClient.e2eeEnrollmentRejectPost = makeOperation('post', '/e2ee/enrollments/{requestId}/reject', WRITE_PARAMS, ['requestId']);

    // Invite links (INVITE-LINK-PASSKEY-FIRST.md §1.3, 2026-10-08). The
    // recipient's GET is addressed by the OWNER's id (`owner`), the owner's
    // list by email — separate operations, each sending only its own key.
    apigClient.e2eeInvitesPost = makeOperation('post', '/e2ee/invites', WRITE_PARAMS);
    apigClient.e2eeInvitesListGet = makeOperation('get', '/e2ee/invites', READ_PARAMS);
    apigClient.e2eeInviteGet = makeOperation('get', '/e2ee/invites/{inviteId}', ['owner'], ['inviteId']);
    apigClient.e2eeInviteClaimPost = makeOperation('post', '/e2ee/invites/{inviteId}/claim', WRITE_PARAMS, ['inviteId']);
    apigClient.e2eeInviteRevokePost = makeOperation('post', '/e2ee/invites/{inviteId}/revoke', WRITE_PARAMS, ['inviteId']);

    return apigClient;
};

export default apigClientFactory
